import {
  JsonSchemaProperty,
  PrimitiveValueType,
  RowDataObject,
  RowScopedErrorsState,
  RowData,
  JsonSchemaItems,
  RowDataErrors,
  RowDataReducerActionPayload,
  RowDataReducerState,
  RowDataValueType,
  NestedObjectErrorState,
} from "../../../types";
import {
  validateCoRelationRowDataInput,
  validateRowDataInput,
} from "../../../pages/LandingPage/validations";

export const updateFieldAction = (
  payload: Partial<RowDataReducerActionPayload>,
  state: RowDataReducerState,
  property: JsonSchemaProperty,
): RowDataReducerState => {
  const {
    propertyKey,
    newFieldValue,
    rowIndex,
    propertySubKey,
    isDialogField,
    selectedTabSectionKey,
  } = payload;

  if (!propertyKey || newFieldValue === undefined) return state;

  if (isDialogField) {
    return updateDialogField(propertyKey, newFieldValue, property, state);
  }

  if (rowIndex !== undefined && propertySubKey && selectedTabSectionKey) {
    return updateNestedArrayObjectField(
      propertyKey,
      newFieldValue,
      propertySubKey,
      rowIndex,
      selectedTabSectionKey,
      property,
      state,
    );
  }

  if (rowIndex !== undefined && propertySubKey) {
    return updateArrayObjectField(
      propertyKey,
      newFieldValue,
      propertySubKey,
      rowIndex,
      property,
      state,
    );
  }

  if (rowIndex === undefined && propertySubKey) {
    return updateObjectSubField(
      propertyKey,
      propertySubKey,
      newFieldValue,
      property,
      state,
    );
  }

  if (selectedTabSectionKey) {
    return updateNestedObjectField(
      propertyKey,
      newFieldValue,
      selectedTabSectionKey,
      property,
      state,
    );
  }

  return updateObjectField(propertyKey, newFieldValue, property, state);
};

const updateNestedArrayObjectField = (
  propertyKey: string,
  newFieldValue: RowDataValueType,
  propertySubKey: string,
  rowIndex: number,
  selectedTabSectionKey: string,
  property: JsonSchemaProperty,
  state: RowDataReducerState,
): RowDataReducerState => {
  const currentArray =
    ((state.rowData[selectedTabSectionKey] as RowData)[
      propertyKey
    ] as RowDataObject[]) ?? [];

  const updatedArray = currentArray.map((item, index) =>
    index === rowIndex ? { ...item, [propertySubKey]: newFieldValue } : item,
  );

  const updatedRowData = {
    ...state.rowData,
    [selectedTabSectionKey]: {
      ...(state.rowData[selectedTabSectionKey] as RowData),
      [propertyKey]: updatedArray,
    },
  } as RowData;

  const rowErrors =
    (state.rowDataErrors[propertyKey] as RowScopedErrorsState) ?? {};

  const updatedErrors: RowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: {
      ...rowErrors,
      [rowIndex]: {
        ...rowErrors[rowIndex],
        [propertySubKey]: validateCoRelationRowDataInput({
          propertyKey,
          propertySubKey,
          rowIndex,
          newFieldValue,
          items: property.items as JsonSchemaItems,
          selectedTabSectionKey,
          rowDataReducerState: { ...state, rowData: updatedRowData },
        }),
      },
    },
  };

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedErrors,
  };
};

const updateArrayObjectField = (
  propertyKey: string,
  newFieldValue: RowDataValueType,
  propertySubKey: string,
  rowIndex: number,
  property: JsonSchemaProperty,
  state: RowDataReducerState,
): RowDataReducerState => {
  const currentArray = (state.rowData[propertyKey] as RowDataObject[]) ?? [];

  const updatedArray = currentArray.map((item, index) =>
    index === rowIndex ? { ...item, [propertySubKey]: newFieldValue } : item,
  );

  const updatedRowData = {
    ...state.rowData,
    [propertyKey]: updatedArray,
  } as RowData;

  const rowErrors =
    (state.rowDataErrors[propertyKey] as RowScopedErrorsState) || {};

  const updatedErrors: RowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: {
      ...rowErrors,
      [rowIndex]: {
        ...rowErrors[rowIndex],
        [propertySubKey]: validateCoRelationRowDataInput({
          propertyKey,
          propertySubKey,
          rowIndex,
          newFieldValue,
          items: property.items as JsonSchemaItems,
          selectedTabSectionKey: undefined,
          rowDataReducerState: { ...state, rowData: updatedRowData },
        }),
      },
    },
  };

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedErrors,
  };
};

const updateNestedObjectField = (
  propertyKey: string,
  newFieldValue: RowDataValueType,
  selectedTabSectionKey: string,
  property: JsonSchemaProperty,
  state: RowDataReducerState,
): RowDataReducerState => {
  const updatedNestedRowData = {
    ...state.rowData,
    [selectedTabSectionKey]: {
      ...(state.rowData[selectedTabSectionKey] as RowDataObject),
      [propertyKey]: newFieldValue,
    },
  } as RowData;

  const updatedErrors: RowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: validateRowDataInput(propertyKey, property, newFieldValue, {
      ...state,
      rowData: updatedNestedRowData,
    }),
  };

  return {
    ...state,
    rowData: updatedNestedRowData,
    rowDataErrors: updatedErrors,
  };
};

const updateObjectField = (
  propertyKey: string,
  newFieldValue: RowDataValueType,
  property: JsonSchemaProperty,
  state: RowDataReducerState,
): RowDataReducerState => {
  const updatedRowData: RowData = {
    ...state.rowData,
    [propertyKey]: newFieldValue,
  };

  const updatedErrors: RowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: validateRowDataInput(propertyKey, property, newFieldValue, {
      ...state,
      rowData: updatedRowData,
    }),
  };

  property?.uniqueCombination?.forEach((combinationPropertyKey) => {
    if (combinationPropertyKey === propertyKey) return;

    const combinationProperty = state.schema.properties[combinationPropertyKey];
    if (!combinationProperty) return;

    updatedErrors[combinationPropertyKey] = validateRowDataInput(
      combinationPropertyKey,
      combinationProperty,
      updatedRowData[combinationPropertyKey],
      { ...state, rowData: updatedRowData },
    );
  });

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedErrors,
  };
};

const updateObjectSubField = (
  propertyKey: string,
  propertySubKey: string,
  newFieldValue: RowDataValueType,
  property: JsonSchemaProperty,
  state: RowDataReducerState,
): RowDataReducerState => {
  const existingObject = (state.rowData[propertyKey] as RowDataObject) ?? {};
  const updatedObject: RowDataObject = {
    ...existingObject,
    [propertySubKey]: newFieldValue as PrimitiveValueType,
  };

  const updatedRowData: RowData = {
    ...state.rowData,
    [propertyKey]: updatedObject,
  };

  const existingRowDataErrors =
    (state.rowDataErrors[propertyKey] as NestedObjectErrorState) || {};
  const updatedProperty = property.properties?.[
    propertySubKey
  ] as JsonSchemaProperty;

  const updatedNestedErrors: NestedObjectErrorState = {
    ...existingRowDataErrors,
    [propertySubKey]: validateRowDataInput(
      propertySubKey,
      updatedProperty,
      updatedObject[propertySubKey],
      {
        ...state,
        rowData: updatedRowData,
      },
    ),
  };

  const updatedErrors: RowDataErrors = {
    ...state.rowDataErrors,
    [propertyKey]: updatedNestedErrors,
  };

  return {
    ...state,
    rowData: updatedRowData,
    rowDataErrors: updatedErrors,
  };
};

const updateDialogField = (
  propertyKey: string,
  newFieldValue: RowDataValueType,
  property: JsonSchemaProperty,
  state: RowDataReducerState,
): RowDataReducerState => {
  const updatedDialogRowData = {
    ...state.dialogRowData,
    [propertyKey]: newFieldValue,
  };

  const updatedDialogErrors = {
    ...state.dialogRowDataErrors,
    [propertyKey]: validateRowDataInput(propertyKey, property, newFieldValue, {
      ...state,
      dialogRowData: updatedDialogRowData,
    }),
  };

  return {
    ...state,
    dialogRowData: updatedDialogRowData,
    dialogRowDataErrors: updatedDialogErrors,
  };
};
